import { useMemo } from 'react'
import {
  selectCurrentProject,
  useSequencerStore,
  type PadContent,
} from '@/stores/sequencerStore'
import { getInstrument } from '@/audio/plugins/registry'
import { ParamSchemaForm } from '@/components/werkshop/ParamSchemaForm'

/**
 * Plugin-pane view for instrument-kind pads (Deep Sub / Drone /
 * Pluck Pad / any future registry instrument except the sampler,
 * which has its own Slicer embedding).
 *
 * Priority (2) consumer in `PluginPane`: when the focused pad is
 * `kind: 'instrument'` and the registered plugin exposes a
 * `paramSchema`, we render the FULL schema in a single-column,
 * scrollable layout. The Cell Editor's Pad tab still surfaces the
 * same schema in compact 2-column mode for quick tweaks; this view
 * is the "depth" companion — every param + the plugin's label +
 * the pad number, without viewport-crowding the Cell Editor.
 *
 * Reuses the generic `ParamSchemaForm` renderer so adding a new
 * instrument with a new schema "just works" here — no per-plugin
 * React. Writes go through `setPadContent` just like the Cell
 * Editor path, so both surfaces edit the same `PadContent.preset`
 * blob and stay in sync round-trip.
 */
interface InstrumentPluginViewProps {
  padIndex: number
  /** When true, expand to available full-screen modal height. The
   *  embedded (tab-slot) path leaves it undefined; the modal wrapper
   *  in `SequencerTabs` passes true. Purely a styling nudge — the
   *  form itself doesn't care. */
  fullScreen?: boolean
}

export function InstrumentPluginView({
  padIndex,
  fullScreen = false,
}: InstrumentPluginViewProps) {
  const project = useSequencerStore(selectCurrentProject)
  const setPadContent = useSequencerStore(s => s.setPadContent)
  const padDef = project?.padDefs?.[padIndex]

  const plugin = useMemo(() => {
    if (!padDef || padDef.kind !== 'instrument') return null
    return getInstrument(padDef.instrumentId) ?? null
  }, [padDef])

  if (!padDef || padDef.kind !== 'instrument' || !plugin) {
    return (
      <div className="flex flex-1 min-h-[20rem] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-brd-subtle bg-surface-sunken/60 px-6 py-12 text-center">
        <p className="text-sm font-medium text-content-primary">
          No instrument on this pad
        </p>
        <p className="max-w-md text-xs text-content-secondary">
          Open the Cell Editor&apos;s Pad tab to pick an instrument for pad{' '}
          {padIndex + 1}.
        </p>
      </div>
    )
  }

  const handleParamChange = (paramId: string, value: number) => {
    // Narrow the union here because `padDef` above is already
    // `kind: 'instrument'`, but `setPadContent` wants the full
    // discriminated shape.
    const next: PadContent = {
      ...padDef,
      preset: {
        ...(padDef.preset ?? {}),
        [paramId]: value,
      },
    }
    setPadContent(padIndex, next)
  }

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border border-brd-subtle bg-surface-sunken p-4 ${
        fullScreen ? 'h-full' : 'flex-1 min-h-[20rem]'
      }`}
    >
      <header className="flex items-baseline gap-3 border-b border-brd-subtle pb-2">
        <span className="text-xs font-mono uppercase tracking-wider text-content-secondary">
          Pad {padIndex + 1}
        </span>
        <span className="text-sm font-semibold text-content-primary">
          {plugin.label}
        </span>
        <span className="flex-1" />
        {padDef.label && (
          <span
            className="truncate text-[10px] text-content-tertiary"
            title={padDef.label}
          >
            {padDef.label}
          </span>
        )}
      </header>
      <div className="flex-1 overflow-auto pr-1">
        <ParamSchemaForm
          schema={plugin.paramSchema}
          values={(padDef.preset ?? {}) as Record<string, number | string>}
          onChange={handleParamChange}
        />
      </div>
    </div>
  )
}
